import type { DeviceState } from '@kw/device-service/dist/device/entities/deviceState.entity';
import { Checkbox, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useContext, useState } from 'react';
import { useDebounce } from 'use-debounce';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CheckBoxOutlined as CheckBoxOutlinedIcon,
  IndeterminateCheckBoxOutlined as IndeterminateCheckBoxOutlinedIcon,
} from '@mui/icons-material';
import {
  QTableHead,
  QContainer,
  QTableRow,
  QTableCell,
  QTableBody,
  QTable,
  QTableSortableColumnHeader,
  QAsyncTablePagination,
  QSearchBar,
  QButton,
  QSelect,
  QThemeProvider,
  QRangeSelect,
  QChip,
} from '@kw/quokka-ui';
import { ResetButton } from '../shared/ResetButton';
import { useDeviceFilterParams } from './useDeviceSortParams';

import { KwFilterWrapper } from '../../kw-ui-components/KwFilterWrapper';
import { PageLoader } from '../../PageLoader';
import { createQueryString } from '../../utils/createQueryString';
import { getMostRecentState } from '../../utils/getMostRecentState';
import { riskLevelMap } from '../../utils/privacyLevelMap';
import { useStickyResult } from '../../utils/useStickyResult';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { AddDeviceOwnerButton } from '../DeviceOwners/AddDeviceOwnerButton';
import { AddDeviceOwnerModal } from '../DeviceOwners/AddDeviceOwnerModal';
import { CenteringContainer } from '../shared/CenteringContainer';
import { useFetchService } from '../../utils/fetchService';
import { UnenrollDeviceConfirmationModal } from './UnenrollDeviceConfirmationModal';
import { DevicesTableToolbar } from './DevicesTableToolbar';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { formatPlatform } from '../../utils/formatPlatform';
import { BackgroundProgressBar } from '../BackgroundProgressBar/BackgroundProgressBar';
import { NavigationContext } from '../Navigation/NavigationProvider';
import { useQPagination } from '../../kw-ui-components/Pagination/useQPagination';
import { useKwRangeSelect } from '../../kw-ui-components/KwRangeSelect/KwRangeSelect';
import { handleUserSelection } from '../../utils/handleUserSelection';
import { getFilterValue, getMultipleFilterValues, getPlatformFilterValue } from '../../utils/filterHelpers';

export const useDevices = (queryParams, accessToken) => {
  const queryParamString = createQueryString(queryParams);
  const { data: devicesData, error: devicesError, mutate } = useFetchService(`/device/bulk?${queryParamString}`, accessToken);

  const { devices, total } = useStickyResult(devicesData);
  const isLoading = !devicesData && !devicesError;

  return { devices, devicesError, total, isLoading, mutate };
};

export default function Devices() {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDeviceOwnerModal, setShowAddDeviceOwnerModal] = useState(false);
  const [showUnenrollDeviceConfirmationModal, setShowUnenrollDeviceConfirmationModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState({} as any);
  const [multipleSelected, setMultipleSelected] = useState([]);
  const { setDeviceDetails } = useContext(NavigationContext);

  const { page, pageSize, setPage, handleChangePage, handleChangeRowsPerPage } = useQPagination(10);

  const [platform, setPlatform] = useState(['all']);
  const [platformVersion, setPlatformVersion] = useState(['all']);
  const securityRiskLevelRangeHook = useKwRangeSelect([0, 10]);
  const privacyRiskLevelRangeHook = useKwRangeSelect([0, 10]);
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  const {
    deviceNameOrder,
    toggleDeviceNameOrder,
    osOrder,
    toggleOsOrder,
    securityRiskLevelOrder,
    toggleSecurityRiskLevelOrder,
    privacyRiskLevelOrder,
    togglePrivacyRiskLevelOrder,
    numberOfAppsOrder,
    toggleNumberOfAppsOrder,
    resetToggles,
  } = useDeviceFilterParams();

  const os = getPlatformFilterValue(platform);
  const osVersion = getMultipleFilterValues(platformVersion);

  const { devices, total, devicesError, isLoading, mutate } = useDevices(
    {
      page: String(page),
      pageSize: String(pageSize),
      minSecurityRiskLevel: securityRiskLevelRangeHook.value[0],
      maxSecurityRiskLevel: securityRiskLevelRangeHook.value[1],
      minPrivacyRiskLevel: privacyRiskLevelRangeHook.value[0],
      maxPrivacyRiskLevel: privacyRiskLevelRangeHook.value[1],
      os,
      osVersion,
      query: debouncedSearchQuery,
      deviceNameOrder,
      osOrder,
      securityRiskLevelOrder,
      privacyRiskLevelOrder,
      numberOfAppsOrder,
    },
    accessToken,
  );

  const { data: osVersionData, error: osVersionError } = useFetchService('/device/os-versions', accessToken);

  let tableHeaders = [
    {
      title: 'Device Name',
      onClick: toggleDeviceNameOrder,
      sortValue: deviceNameOrder,
    },
    { title: 'Platform', onClick: toggleOsOrder, sortValue: osOrder },
    { title: 'Platform Version', onClick: () => {}, sortValue: null },
    {
      title: '# of Apps',
      onClick: toggleNumberOfAppsOrder,
      sortValue: numberOfAppsOrder,
    },
    {
      title: 'Security Risk Level',
      onClick: toggleSecurityRiskLevelOrder,
      sortValue: securityRiskLevelOrder,
    },
    { title: 'Privacy Risk Level', onClick: togglePrivacyRiskLevelOrder, sortValue: privacyRiskLevelOrder },
    { title: 'Action', onClick: () => {}, sortValue: null },
  ];

  if (!verifyAuthPermission(AuthPermissions.LIST_DEVICE_APPS)) {
    tableHeaders = tableHeaders.filter(header => header.title !== '# of Apps');
  }

  const osVersionsSelectData = [
    { value: 'all', label: 'All' },
    ...(osVersionData?.osVersions || [])
      .filter(version => version)
      .map(version => ({
        value: version,
        label: version,
      })),
  ];

  const osSelectData = [
    { value: 'all', label: 'Both' },
    { value: 'android', label: 'Android' },
    { value: 'ios', label: 'iOS' },
  ];

  const totalPageCount = Math.ceil(total / pageSize);

  if (devicesError || osVersionError) {
    if (devicesError) console.error(devicesError);
    if (osVersionError) console.error(osVersionError);

    return <div>failed to load</div>;
  }
  const resetFilterAndSorts = () => {
    resetToggles();
    setPage(0);
    setPlatform(['all']);
    setPlatformVersion(['all']);
    securityRiskLevelRangeHook.reset();
    privacyRiskLevelRangeHook.reset();
    setSearchQuery('');
  };

  if (!devices || !osVersionData) {
    return <PageLoader />;
  }

  const isSelected = (id: number) => multipleSelected.some(selection => selection.id === id);

  const selectDevice = ({ id, name }: { id: number; name: string }) => {
    const selectedIndex = multipleSelected.findIndex(selection => selection.id === id);

    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(multipleSelected, { id, name });
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(multipleSelected.slice(1));
    } else if (selectedIndex === multipleSelected.length - 1) {
      newSelected = newSelected.concat(multipleSelected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(multipleSelected.slice(0, selectedIndex), multipleSelected.slice(selectedIndex + 1));
    }

    setMultipleSelected(newSelected);
  };

  const selectAllDevices = (event?) => {
    if (event.target.checked) {
      const newSelectedDevices = devices.map(({ id, name }) => ({ id, name }));
      setMultipleSelected(newSelectedDevices);
      return;
    }
    setMultipleSelected([]);
  };

  return (
    <>
      {isLoading && <PageLoader />}
      <QContainer
        sx={{
          maxWidth: '100% !important',
        }}
      >
        <BackgroundProgressBar />
        <KwFilterWrapper>
          <QSelect
            multiple
            label="Platform"
            options={osSelectData}
            value={platform}
            onChange={e => {
              handleUserSelection(platform, e.target.value, setPlatform);
              setPage(0);
            }}
            formControlProps={{ sx: { width: '115px' } }}
          />
          <QSelect
            multiple
            label="Platform Version"
            options={osVersionsSelectData}
            value={platformVersion}
            onChange={e => {
              handleUserSelection(platformVersion, e.target.value, setPlatformVersion);
              setPage(0);
            }}
            formControlProps={{ sx: { width: '165px' } }}
          />
          <QRangeSelect
            label="Security Risk Level"
            sliderAriaLabel="security-risk-score-range-slider"
            {...securityRiskLevelRangeHook}
            handleChange={args => {
              securityRiskLevelRangeHook.handleChange(args);
              setPage(0);
            }}
            formControlProps={{ sx: { width: '190px' } }}
          />
          <QRangeSelect
            label="Privacy Risk Level"
            sliderAriaLabel="security-risk-score-range-slider"
            {...privacyRiskLevelRangeHook}
            handleChange={args => {
              privacyRiskLevelRangeHook.handleChange(args);
              setPage(0);
            }}
            formControlProps={{ sx: { width: '185px' } }}
          />

          <ResetButton
            onClick={() => {
              resetFilterAndSorts();
            }}
          />

          <AddDeviceOwnerButton onClick={() => setShowAddDeviceOwnerModal(true)} />
          <AddDeviceOwnerModal isOpen={showAddDeviceOwnerModal} onClose={() => setShowAddDeviceOwnerModal(false)} />
          <UnenrollDeviceConfirmationModal
            showConfirmationModal={showUnenrollDeviceConfirmationModal}
            setShowConfirmationModal={setShowUnenrollDeviceConfirmationModal}
            selectedDevice={selectedDevice}
            afterSuccessFunc={mutate}
          />
        </KwFilterWrapper>
        <div>
          <QSearchBar
            searchValue={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                setSearchQuery(e.target.value);
                setPage(0);
              }
            }}
            onRemove={() => {
              setSearchQuery('');
              setPage(0);
            }}
            placeholder="Search devices"
          />

          <DevicesTableToolbar
            numSelected={multipleSelected.length}
            selectedDevices={multipleSelected}
            onClickSelectAll={selectAllDevices}
            afterSuccessFunc={() => {
              mutate();
              setMultipleSelected([]);
            }}
          />

          {total > 10 && (
            <QAsyncTablePagination
              page={page + 1}
              pagination={{
                dataPerPage: pageSize,
                totalData: total,
                lastPage: totalPageCount,
              }}
              getPage={newPage => setPage(newPage - 1)}
              showRowsPerPage={true}
              currentRows={devices.length}
              handlePageSizeChange={handleChangeRowsPerPage}
              pageSize={pageSize}
              pageSizeOptions={[10, 20, 50, 100].map(v => ({ label: v, value: v }))}
            />
          )}
          <QTable aria-label="Devices-Table">
            <QTableHead>
              <QTableRow>
                <QTableCell padding="checkbox">
                  <Checkbox
                    indeterminate={multipleSelected.length > 0 && multipleSelected.length < devices.length}
                    checked={devices.length > 0 && multipleSelected.length === devices.length}
                    onChange={selectAllDevices}
                    inputProps={{
                      'aria-label': 'select all devices',
                    }}
                    indeterminateIcon={<IndeterminateCheckBoxOutlinedIcon />}
                  />
                </QTableCell>
                {tableHeaders.map(({ title, onClick, sortValue }) => (
                  <QTableSortableColumnHeader
                    key={title}
                    title={title}
                    onClick={() => {
                      onClick();
                      setPage(0);
                    }}
                    sortValue={sortValue}
                  />
                ))}
              </QTableRow>
            </QTableHead>
            <QTableBody>
              {devices.map((device, index) => {
                const deviceState = getMostRecentState<DeviceState>(device.states);
                const isItemSelected = isSelected(device.id);
                const labelId = `device-table-checkbox-${index}`;
                return (
                  <QTableRow key={device.id} selected={isItemSelected}>
                    <QTableCell
                      onClick={() => selectDevice({ id: device.id, name: device.name })}
                      tabIndex={-1}
                      key={device.id}
                      selected={isItemSelected}
                    >
                      <Checkbox
                        checked={isItemSelected}
                        inputProps={{
                          'aria-labelledby': labelId,
                          'aria-label': `${device.name} checkbox`,
                          'aria-checked': isItemSelected,
                        }}
                        checkedIcon={<CheckBoxOutlinedIcon />}
                      />
                    </QTableCell>
                    <QTableCell selected={deviceNameOrder}>
                      <CenteringContainer horizontal={false}>
                        <Link
                          underline="hover"
                          href={`#/device-details?deviceId=${device.id}&deviceName=${device.name}`}
                          onClick={() => {
                            setDeviceDetails({ deviceId: device.id, deviceName: device.name });
                          }}
                        >
                          {device.name}
                        </Link>
                      </CenteringContainer>
                    </QTableCell>
                    <QTableCell selected={osOrder}>{formatPlatform(device.os)}</QTableCell>
                    <QTableCell>{deviceState.osVersion}</QTableCell>
                    {verifyAuthPermission(AuthPermissions.LIST_DEVICE_APPS) && (
                      <QTableCell selected={numberOfAppsOrder}>{deviceState.applicationCount}</QTableCell>
                    )}
                    <QTableCell selected={securityRiskLevelOrder}>
                      <StyledRiskLevel label={deviceState.securityRiskLevel} />
                    </QTableCell>
                    <QTableCell selected={privacyRiskLevelOrder}>
                      <StyledRiskLevel label={deviceState.privacyRiskLevel} />
                    </QTableCell>
                    <QTableCell>
                      <QButton
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => {
                          setSelectedDevice(device);
                          setShowUnenrollDeviceConfirmationModal(!showUnenrollDeviceConfirmationModal);
                        }}
                      />
                    </QTableCell>
                  </QTableRow>
                );
              })}
            </QTableBody>
          </QTable>
        </div>
      </QContainer>
    </>
  );
}

/** styles */
const StyledRiskLevel = styled(QChip)<{ label: string }>`
  color: white;
  border-radius: 12px;
  width: 52px;
  height: 24px;
  font-size: 12px;
  background-color: ${props => riskLevelMap(props.theme)[props.label]};
` as typeof QChip;
